import React from 'react';
import I18n from 'i18n-js';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCCard, RCFlex, RCIcon, RCText, buttonClassName } from '@reverbdotcom/cadence/components';
import { ArrowsClockwiseIcon, GlobeIcon, SparkleIcon, NudgeBoxIcon } from '@reverbdotcom/cadence/icons/react';
import { Paths } from './shared/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';

const COMPONENT_NAME = 'SellToFund';

type Variant = 'logged-out-homepage';

interface Props {
  variant?: Variant;
}

function SellToFundCTA() {
  const clickEvent = {
    eventName: MParticleEventName.ClickedComponent,
    componentName: COMPONENT_NAME,
  };

  return (
    <CoreLink
      to={Paths.sellFormSearch.expand({})}
      clickEvent={clickEvent}
      className={buttonClassName({
        variant: 'filled',
        size: { desktop: 'large', tablet: 'medium' },
        fullWidth: { mobile: true },
      })}
    >
      {I18n.t('discovery.sellToFund.cta')}
    </CoreLink>
  );
}

function ValuePropsSection() {
  const valueProps = [
    { icon: <GlobeIcon />, title: 'valuePropOne', description: 'valuePropOneDescription' },
    { icon: <SparkleIcon />, title: 'valuePropTwo', description: 'valuePropTwoDescription' },
    { icon: <NudgeBoxIcon />, title: 'valuePropThree', description: 'valuePropThreeDescription' },
  ];

  return (
    <div
      className="bd-radius-lg d-flex fx-dir-col gap-8 mobile-gap-4 padding-8 tablet-padding-6 mobile-padding-4"
      style={{ backgroundColor: 'var(--rc-color-palette-teal-100)' }}
    >
      {valueProps.map(({ icon, title, description }, index) => (
        <div key={title} className={`d-flex gap-4 ${index > 0 ? 'mt-4' : ''}`}>
          <div>
            <RCIcon size={{ desktop: 9, mobile: 7 }} icon={icon} />
          </div>
          <div className="d-flex fx-dir-col gap-2">
            <RCText.Utility size={{ desktop: '500', mobile: '450' }} weight="bold">
              {I18n.t(`discovery.sellToFund.${title}`)}
            </RCText.Utility>
            <RCText.Utility size={{ desktop: '450', mobile: '400' }}>
              {I18n.t(`discovery.sellToFund.${description}`)}
            </RCText.Utility>
          </div>
        </div>
      ))}
    </div>
  );
}

function SellToFundOrderConfirmation() {
  return (
    <RCCard>
      <div className="padding-6 mobile-padding-4 fg-color-text-primary">
        <RCFlex
          gap={{ desktop: '4', mobile: '6' }}
          justify="space-between"
          direction={{ mobile: 'col' }}
          align={{ desktop: 'center', mobile: 'stretch' }}
        >
          <RCFlex gap={{ desktop: '4', mobile: '2' }} direction={{ mobile: 'col' }}>
            <div className="fx-align-self-start">
              <RCIcon size={7} icon={<ArrowsClockwiseIcon />} />
            </div>
            <RCFlex gap={{ desktop: '2', mobile: '1' }} direction="col">
              <RCText.Utility size={{ desktop: '500', mobile: '450' }} weight="bold">
                {I18n.t('discovery.sellToFund.title')}
              </RCText.Utility>
              <RCText.Utility size={{ desktop: '450', mobile: '400' }} weight="regular">
                {I18n.t('discovery.sellToFund.subtitle')}
              </RCText.Utility>
            </RCFlex>
          </RCFlex>
          <div className="height-100">
            <SellToFundCTA />
          </div>
        </RCFlex>
      </div>
    </RCCard>
  );
}

function SellToFundLoggedOutHomepage() {
  return (
    <div className="site-wrapper gap-row mb-gap-row">
      <div
        className="bd-radius-lg d-flex fx-justify-between mobile-fx-dir-col mobile-padding-4 tablet-padding-8 padding-12 gap-8 mobile-gap-0"
        style={{ backgroundColor: 'var(--rc-color-palette-teal-300)' }}
      >
        <div style={{ maxWidth: '700px' }}>
          <div className="pb-4">
            <RCIcon size={{ desktop: 9, mobile: 7 }} icon={<ArrowsClockwiseIcon />} />
          </div>
          <RCText.Title size={{ desktop: '1200', mobile: '600' }}>
            {I18n.t('discovery.sellToFund.title')}
          </RCText.Title>
          <div className="ptb-2 mobile-pb-8">
            <RCText variant={{ desktop: 'title-700-regular', mobile: 'utility-450' }}>
              {I18n.t('discovery.sellToFund.subtitle')}
            </RCText>
          </div>
          <div className="mt-8 d-block mobile-d-none">
            <SellToFundCTA />
          </div>
        </div>
        <div style={{ maxWidth: '650px' }}>
          <ValuePropsSection />
          <div className="mt-8 d-none mobile-d-block">
            <SellToFundCTA />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SellToFund({ variant }: Props) {
  if (variant === 'logged-out-homepage') {
    return <SellToFundLoggedOutHomepage />;
  }

  return <SellToFundOrderConfirmation />;
}
